// @flow
import { isEmpty } from 'lodash';
import { reduce, makeActionCreator } from '../helpers/utils';
import ApiClient from '../api/Api';
import { notify } from '../helpers/BugsnagClient';

const debug = require('debug')('jp:reducers:vehicle');

export const TOGGLE_ALL_VEHICLES = 'vehicle/TOGGLE_ALL_VEHICLES';
export const CREATE_VEHICLE = 'vehicle/CREATE_VEHICLE';
export const CREATE_VEHICLE_SUCCESS = 'vehicle/CREATE_VEHICLE_SUCCESS';
export const CREATE_VEHICLE_FAIL = 'vehicle/CREATE_VEHICLE_FAIL';
export const CANCEL_VEHICLE_EDIT = 'vehicle/CANCEL_VEHICLE_EDIT';
export const DELETE_VEHICLE = 'vehicle/DELETE_VEHICLE';
export const DELETE_VEHICLE_SUCCESS = 'vehicle/DELETE_VEHICLE_SUCCESS';
export const DELETE_VEHICLE_FAIL = 'vehicle/DELETE_VEHICLE_FAIL';
export const SELECT_VEHICLE = 'vehicle/SELECT_VEHICLE';
export const TOGGLE_USER_ADD_VEHICLE = 'vehicle/TOGGLE_USER_ADD_VEHICLE';
export const CANCEL_REGISTRATION_INPUT = 'vehicle/CANCEL_REGISTRATION_INPUT';
export const LOAD_VEHICLE = 'vehicle/LOAD_VEHICLE';
export const LOAD_VEHICLE_SUCCESS = 'vehicle/LOAD_VEHICLE_SUCCESS';
export const LOAD_VEHICLE_FAIL = 'vehicle/LOAD_VEHICLE_FAIL';
export const LOAD_VEHICLE_LOOKUP = 'vehicle/LOAD_VEHICLE_LOOKUP';
export const LOAD_VEHICLE_LOOKUP_SUCCESS =
  'vehicle/LOAD_VEHICLE_LOOKUP_SUCCESS';
export const LOAD_VEHICLE_LOOKUP_FAIL = 'vehicle/LOAD_VEHICLE_LOOKUP_FAIL';
export const CLEAR_GLOBAL_ERROR = 'checkout/CLEAR_GLOBAL_ERROR';

export type Vehicle = {|
  id: ?number,
  make: string,
  model: string,
  modelId: number,
  registration: string,
  fuelType: ?string,
  isHired: ?string,
  hireCompany: ?string,
  isPrimary: boolean,
  createdAt: string,
  isNew: boolean,
  isActive: boolean,
  editing: boolean
|};

type Action =
  | {| type: typeof CREATE_VEHICLE |}
  | {| type: typeof CREATE_VEHICLE_SUCCESS, payload: Object |}
  | {| type: typeof CREATE_VEHICLE_FAIL |}
  | {| type: typeof DELETE_VEHICLE |}
  | {| type: typeof DELETE_VEHICLE_SUCCESS, id: number |}
  | {| type: typeof DELETE_VEHICLE_FAIL |}
  | {| type: typeof TOGGLE_ALL_VEHICLES |}
  | {| type: typeof CANCEL_VEHICLE_EDIT, id: number, isNew: boolean |}
  | {| type: typeof SELECT_VEHICLE, id: number |}
  | {| type: typeof CANCEL_REGISTRATION_INPUT |}
  | {| type: typeof LOAD_VEHICLE |}
  | {| type: typeof LOAD_VEHICLE_SUCCESS, payload: { data: Object } |}
  | {| type: typeof LOAD_VEHICLE_FAIL |}
  | {| type: typeof LOAD_VEHICLE_LOOKUP, registration: string |}
  | {|
      type: typeof LOAD_VEHICLE_LOOKUP_SUCCESS,
      payload: Object,
      registration: string
    |}
  | {| type: typeof LOAD_VEHICLE_LOOKUP_FAIL, registration: string |}
  | {| type: typeof CLEAR_GLOBAL_ERROR |};

export type State = {|
  userAddingVehicle: boolean,
  userEditingVehicle: boolean,
  displayAllVehicles: boolean,
  loadingVehicles: boolean,
  vehicleLookupFailed: boolean,
  createVehicleLoading: boolean,
  vehiclesLoaded: boolean,
  vehicleLookupFailedMsg: string,
  vehicles: Vehicle[],
  vehicleApiError: ?{|
    title: string,
    code: number,
    identifier: string
  |}
|};

export const initialState: State = {
  userAddingVehicle: false,
  userEditingVehicle: false,
  displayAllVehicles: false,
  loadingVehicles: false,
  vehicleLookupFailed: false,
  createVehicleLoading: false,
  vehiclesLoaded: false,
  vehicleLookupFailedMsg: '',
  vehicles: [],
  vehicleApiError: null
};

const getIdxByKey = (array: Object[], key: string, value: any) => {
  let indexOfTempItem = -1;
  array.find((item, idx) => {
    indexOfTempItem = idx;
    return item[key] === value;
  });
  return indexOfTempItem;
};

const getVehicleIdxByModelId = (vehicles, modelId) =>
  getIdxByKey(vehicles, 'modelId', modelId);

export const getIdxById = (array, id) => getIdxByKey(array, 'id', id);

export default reduce(initialState, {
  [CREATE_VEHICLE](state: State): State {
    debug(CREATE_VEHICLE);
    return { ...state, createVehicleLoading: true };
  },
  [CREATE_VEHICLE_SUCCESS](state: State, action: Action): State {
    debug(CREATE_VEHICLE_SUCCESS, action);
    if (action.payload && action.payload.data) {
      const createdVehicle = action.payload.data;

      const existingIds = [...state.vehicles].map(vehicle => vehicle.id);
      const itemToAdd =
        existingIds.indexOf(createdVehicle.id) > -1 ? [] : [createdVehicle];

      const vehicles = [...itemToAdd, ...state.vehicles]
        .filter(vehicle => vehicle.id !== null)
        .map(vehicle => ({
          ...vehicle,
          isActive: createdVehicle.id === vehicle.id,
          editing: false
        }));

      return {
        ...state,
        createVehicleLoading: false,
        userAddingVehicle: false,
        userEditingVehicle: false,
        vehicles: [...vehicles]
      };
    }

    notify(new Error('No vehicle in response'), {
      metaData: action
    });

    return {
      ...state,
      createVehicleLoading: false
    };
  },
  [CREATE_VEHICLE_FAIL](state: State, action: Action): State {
    debug(CREATE_VEHICLE_FAIL, action);
    return {
      ...state,
      createVehicleLoading: false
    };
  },
  [DELETE_VEHICLE](state: State): State {
    debug(DELETE_VEHICLE);
    return { ...state };
  },
  [DELETE_VEHICLE_SUCCESS](state: State, action: Action): State {
    debug(DELETE_VEHICLE_SUCCESS, action);
    if (action && action.id) {
      const { vehicles } = state;
      const newVehicles = [...vehicles];
      const idx = getIdxById(vehicles, action.id);

      newVehicles.splice(idx, 1);

      if (newVehicles.length >= 1) {
        newVehicles[0].isActive = true;
      }
      return {
        ...state,
        vehicles: [...newVehicles],
        userAddingVehicle: isEmpty(newVehicles)
      };
    }

    notify(new Error('No vehicle id in delete action'), {
      metaData: action
    });

    return {
      ...state
    };
  },
  [DELETE_VEHICLE_FAIL](state: State, action: Action): State {
    debug(DELETE_VEHICLE_FAIL, action);
    if (action && action.payload && action.payload.data) {
      return {
        ...state,
        vehicleApiError: action.payload.data
      };
    }
    notify(new Error('Invalid shape for vehicle delete'), {
      metaData: action
    });

    return {
      ...state
    };
  },
  [TOGGLE_ALL_VEHICLES](state: State): State {
    debug(TOGGLE_ALL_VEHICLES);
    return { ...state, displayAllVehicles: !state.displayAllVehicles };
  },
  [CANCEL_VEHICLE_EDIT](state: State, action: Action): State {
    debug(CANCEL_VEHICLE_EDIT, action);
    if (action && action.id && action.isNew) {
      const vehicles = [...state.vehicles];

      // If vehicle is new and unsaved then we just remove it from the arr
      if (action.isNew) {
        vehicles.splice(getVehicleIdxByModelId(vehicles, action.id), 1);

        if (vehicles[0]) {
          vehicles[0].isActive = true;
        }
      }
      // Else we just toggle its editing state
      if (!action.isNew) {
        const idx = getIdxById(vehicles, action.id);
        vehicles[idx] = { ...vehicles[idx], editing: false };
      }
      return {
        ...state,
        userEditingVehicle: false,
        vehicles: [...vehicles],
        userAddingVehicle: vehicles.length === 0
      };
    }

    notify(new Error('No payload in cancel vehicle action'), {
      metaData: action
    });

    return {
      ...state,
      userEditingVehicle: false
    };
  },
  [SELECT_VEHICLE](state: State, action: Action): State {
    debug(SELECT_VEHICLE, action);
    if (action && action.id) {
      const { id } = action;
      const { vehicles } = state;

      return {
        ...state,
        vehicles: [
          ...vehicles
            .filter(vehicle => vehicle.isNew !== true)
            .map(vehicle => ({
              ...vehicle,
              isActive: vehicle.id === id
            }))
        ]
      };
    }
    return state;
  },
  [TOGGLE_USER_ADD_VEHICLE](state: State): State {
    debug(TOGGLE_USER_ADD_VEHICLE);
    return {
      ...state,
      userAddingVehicle: !state.userAddingVehicle,
      vehicleLookupFailed: false,
      vehicleLookupFailedMsg: ''
    };
  },
  [CANCEL_REGISTRATION_INPUT](state: State, action: Action): State {
    debug(CANCEL_REGISTRATION_INPUT, action);
    return {
      ...state,
      userAddingVehicle: false
    };
  },
  [LOAD_VEHICLE](state: State): State {
    debug(LOAD_VEHICLE);
    return { ...state, loadingVehicles: true };
  },
  [LOAD_VEHICLE_SUCCESS](state: State, action: Action): State {
    debug(LOAD_VEHICLE_SUCCESS, action);
    if (action.payload && action.payload.data) {
      const rawVehicles = action.payload.data;
      const vehicles = rawVehicles.map(item => ({
        ...item,
        isActive: false,
        editing: false,
        isNew: false
      }));
      if (vehicles[0]) {
        vehicles[0].isActive = true;
      }

      return {
        ...state,
        userAddingVehicle: isEmpty(vehicles),
        loadingVehicles: false,
        vehiclesLoaded: true,
        vehicles: [...vehicles]
      };
    }
    throw new Error('No listing in checkout listing details response');
  },
  [LOAD_VEHICLE_FAIL](state: State, action: Action): State {
    debug(LOAD_VEHICLE_FAIL, action);
    return {
      ...state,
      loadingVehicles: false,
      vehiclesLoaded: false,
      userAddingVehicle: true
    };
  },
  [LOAD_VEHICLE_LOOKUP](state: State): State {
    debug(LOAD_VEHICLE_LOOKUP);
    return {
      ...state,
      loadingVehicleLookup: true,
      vehicleLookupFailed: false,
      vehicleLookupFailedMsg: ''
    };
  },
  [LOAD_VEHICLE_LOOKUP_SUCCESS](state: State, action: Action): State {
    debug(LOAD_VEHICLE_LOOKUP_SUCCESS, action);
    if (action.payload && action.payload.data && action.registration) {
      const vehicle = {
        ...action.payload.data,
        id: null,
        modelId: action.payload.data.id,
        editing: true,
        isNew: true,
        isActive: false,
        registration: action.registration
      };
      const newVehicles = [
        ...state.vehicles.map(item => ({
          ...item,
          editing: false,
          isNew: false,
          isActive: false
        }))
      ];
      newVehicles.unshift(vehicle);
      return {
        ...state,
        userAddingVehicle: false,
        userEditingVehicle: true,
        loadingVehicleLookup: false,
        vehicleLookupFailed: false,
        vehicleLookupFailedMsg: '',
        vehicles: newVehicles
      };
    }

    notify(new Error('No data in vehicle lookup response'), {
      metaData: action
    });

    return {
      ...state,
      loadingVehicleLookup: false
    };
  },
  [LOAD_VEHICLE_LOOKUP_FAIL](state: State, action: Action): State {
    debug(LOAD_VEHICLE_LOOKUP_FAIL, action);
    if (action.payload && action.payload.data && action.registration) {
      if (action.payload.data.code && action.payload.data.code === 20000) {
        // vehicle not found by experian
        const otherVehicleId = 701;
        const vehicle = {
          id: null,
          modelId: otherVehicleId,
          make: 'Other',
          model: 'Other',
          editing: true,
          isNew: true,
          isActive: false,
          registration: action.registration
        };

        const newVehicles = [
          ...state.vehicles.map(item => ({
            ...item,
            editing: false,
            isNew: false,
            isActive: false
          }))
        ];
        newVehicles.unshift(vehicle);

        return {
          ...state,
          vehicles: newVehicles,
          userAddingVehicle: false,
          userEditingVehicle: true,
          loadingVehicleLookup: false
        };
      }
    }

    return {
      ...state,
      loadingVehicleLookup: false
    };
  },
  [CLEAR_GLOBAL_ERROR](state: State): State {
    debug(CLEAR_GLOBAL_ERROR);
    return {
      ...state,
      vehicleApiError: null
    };
  }
});

export function createUserVehicle({ modelId, registration }: Object) {
  return {
    types: [CREATE_VEHICLE, CREATE_VEHICLE_SUCCESS, CREATE_VEHICLE_FAIL],
    promise: (client: ApiClient) =>
      client.vehicles.create({ modelId, registration }),
    registration,
    modelId
  };
}

export function deleteUserVehicle(id: number) {
  return {
    types: [DELETE_VEHICLE, DELETE_VEHICLE_SUCCESS, DELETE_VEHICLE_FAIL],
    promise: (client: ApiClient) => client.vehicles.deleteVehicle(id),
    id
  };
}

export function createUserVehicleFromRegistration(registration: string) {
  return (dispatch: (*) => Promise<*>) =>
    new Promise((resolve, reject) => {
      dispatch({
        types: [CREATE_VEHICLE, CREATE_VEHICLE_SUCCESS, CREATE_VEHICLE_FAIL],
        promise: (client: ApiClient) =>
          client.vehicles
            .registrationLookup(registration)
            .then(data => data.id)
            .catch(
              () =>
                // If the model lookup request failed for any reason return the default model ID
                701
            )
            .then(modelId => client.vehicles.create({ modelId, registration }))
            .then(data => {
              resolve(data);
              return data;
            })
            .catch(err => {
              reject(err);
              throw err;
            }),
        registration
      });
    });
}

export function loadVehicles() {
  return {
    types: [LOAD_VEHICLE, LOAD_VEHICLE_SUCCESS, LOAD_VEHICLE_FAIL],
    promise: (client: ApiClient) => client.vehicles.getVehicles()
  };
}

export const toggleAllVehicles = makeActionCreator(TOGGLE_ALL_VEHICLES);

export const cancelVehicleEdit = makeActionCreator(
  CANCEL_VEHICLE_EDIT,
  'id',
  'isNew'
);

export function registrationLookup(registration: string) {
  return {
    types: [
      LOAD_VEHICLE_LOOKUP,
      LOAD_VEHICLE_LOOKUP_SUCCESS,
      LOAD_VEHICLE_LOOKUP_FAIL
    ],
    promise: (client: ApiClient) =>
      client.vehicles.registrationLookup(registration),
    registration
  };
}

export const selectVehicle = makeActionCreator(SELECT_VEHICLE, 'id');
export const toggleUserAddVehicle = makeActionCreator(TOGGLE_USER_ADD_VEHICLE);
export const cancelRegistrationInput = makeActionCreator(
  CANCEL_REGISTRATION_INPUT
);

export const acknowledgeVehicleApiError = makeActionCreator(CLEAR_GLOBAL_ERROR);
